'use client'

import { Button } from '@/core/components/form/button/button'
import { Menu } from '@base-ui/react/menu'
import { useState } from 'react'
import {
  LuChevronDown,
  LuChevronUp,
  LuEllipsisVertical,
  LuFlag,
  LuHeart,
  LuPencil,
  LuTrash2,
} from 'react-icons/lu'

import SafeImage from '@/features/veluvit/components/ui/media-image'
import { cn } from '@/core/utils/common'

import CommentInput from '@/features/veluvit/components/comments/comment-input'
import EmptyState from '@/features/veluvit/components/ui/empty-state'
import { ago } from '@/features/veluvit/lib/home'

import type {
  WorkComment,
  WorkComments,
} from '@/features/veluvit/components/comments/use-work-comments'
import type { ReactNode } from 'react'

type MenuAction = {
  key: string
  label: string
  icon: ReactNode
  onClick: () => void
}

/**
 * 댓글 ⋮ 메뉴 — 유튜브처럼 단추 바로 아래 오른쪽 끝에 붙는 작은 불투명 판(아이콘 + 글자).
 * core DropdownMenu 는 단추에서 8px 떨어진 유리 판 · 빨간 글자라 댓글 줄에선 멀리 떠 보이고 튀었다(2026-10-08) — 여기만 따로 둔다.
 */
function CommentMenu({ items }: { items: MenuAction[] }) {
  return (
    <Menu.Root modal={false}>
      <Menu.Trigger
        aria-label="댓글 메뉴"
        className="-mt-1 -mr-2 grid size-8 flex-none cursor-pointer place-items-center rounded-full text-t2 outline-none transition-colors hover:bg-foreground/[0.08] hover:text-t1 focus-visible:bg-foreground/[0.1] data-[popup-open]:bg-foreground/[0.1] data-[popup-open]:text-t1"
      >
        <LuEllipsisVertical aria-hidden="true" className="size-4" />
      </Menu.Trigger>
      <Menu.Portal>
        {/* 레이어(시트 · 모달) 위에 뜬다 */}
        <Menu.Positioner
          side="bottom"
          align="end"
          sideOffset={2}
          className="z-[5000200]"
        >
          <Menu.Popup className="min-w-32 origin-[var(--transform-origin)] rounded-field bg-surface-raised py-1.5 shadow-[0_12px_32px_-8px_rgb(0_0_0/0.7),inset_0_0_0_1px_rgb(255_255_255/0.08)] outline-none transition-[opacity,scale] duration-150 data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0">
            {items.map((item) => (
              <Menu.Item
                key={item.key}
                onClick={item.onClick}
                className="flex cursor-pointer items-center gap-3 px-4 py-2.5 text-c14 text-t1 outline-none transition-colors data-[highlighted]:bg-foreground/[0.08] [&>svg]:size-4 [&>svg]:text-t2"
              >
                {item.icon}
                {item.label}
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}

/** 답글 입력 — 어느 원댓글 묶음(rootId)에, 어느 댓글 바로 아래(targetId)에, 미리 넣을 호출(@이름) */
type Replying = { rootId: string; targetId: string; mention: string }

/**
 * 댓글 목록 — 유튜브 댓글 구조(2026-10-08):
 * - 한 줄 = 머리글자 원 · 이름 · 작가 표시 · 몇 분 전 · (수정됨) / 본문 / ♥ 수 · 답글, 오른쪽 끝 ⋮(내 것 수정 · 삭제, 남의 것 신고).
 * - 답글은 원댓글 아래 `답글 n개 ⌄` 로 접어 두고 누르면 펼친다(묶음은 한 단계).
 * - `답글`을 누르면 그 댓글 바로 아래에 입력칸이 열린다. 답글에 다는 답글은 같은 묶음에 `@이름 ` 을 붙여 단다.
 * - 수정은 그 자리 입력칸(저장 · 취소).
 * 상세 화면 댓글 칸 · 탐색 댓글 패널(PC 오른쪽 · 모바일 바텀시트)이 같이 쓴다.
 */
export default function CommentList({
  c,
  className,
}: {
  c: WorkComments
  className?: string
}) {
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  const [replying, setReplying] = useState<Replying | null>(null)
  const [editing, setEditing] = useState<string | null>(null)

  if (c.failed && !c.items)
    return (
      <div className={className}>
        <EmptyState
          compact
          title="댓글을 불러오지 못했어요"
          action={
            <Button
              type="button"
              onClick={c.retry}
              className="cursor-pointer text-point underline underline-offset-4"
              variant="bare"
            >
              다시 불러오기
            </Button>
          }
        />
      </div>
    )
  if (!c.items)
    return (
      <p className={cn('py-6 text-center text-c13 text-t3', className)}>
        댓글을 불러오는 중…
      </p>
    )

  const roots = c.items.filter((x) => !x.parentId)
  const repliesOf = (parent: string) =>
    (c.items || []).filter((x) => x.parentId === parent)
  const expand = (id: string, on: boolean) =>
    setOpen((prev) => {
      const nextSet = new Set(prev)
      if (on) nextSet.add(id)
      else nextSet.delete(id)
      return nextSet
    })

  const row = (x: WorkComment, rootId: string) => {
    const reply = x.parentId !== null
    const mine = c.mine(x)
    return (
      <div>
        <div className="flex gap-3">
          <span
            aria-hidden="true"
            className={cn(
              'grid flex-none place-items-center rounded-full bg-foreground/[0.12] font-bold text-t1',
              reply ? 'size-6 text-c11' : 'size-8 text-c13'
            )}
          >
            {Array.from(x.author.name)[0] || '?'}
          </span>
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-x-1.5 text-c12 leading-[1.4] text-t2">
              <b className="font-semibold text-t1">{x.author.name}</b>
              {c.creatorId && x.author.id === c.creatorId && (
                <span className="rounded-control bg-point/20 px-1.5 text-c10h font-bold text-point">
                  작가
                </span>
              )}
              <time dateTime={x.createdAt} className="text-t3">
                {ago(x.createdAt)}
              </time>
              {x.editedAt && <span className="text-t3">(수정됨)</span>}
            </p>
            {editing === x.id ? (
              <CommentInput
                className="mt-2"
                small
                alwaysOpen
                autoFocus
                initial={x.text}
                placeholder="댓글 고치기"
                submitLabel="저장"
                onSubmit={(text) => c.edit(x, text)}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <>
                <p className="mt-0.5 text-c14 leading-[1.6] whitespace-pre-line text-t1 [overflow-wrap:anywhere]">
                  {x.text}
                </p>
                {x.snapshot && (
                  <figure className="mt-2 w-28">
                    <div className="relative aspect-[3/4] overflow-hidden rounded-field bg-black">
                      {/* 서버가 권한을 확인하는 개인 이미지라 최적화 서버를 거치지 않는다 */}
                      <SafeImage
                        src={x.snapshot.image}
                        alt="대화 장면 스냅"
                        fill
                        sizes="112px"
                        unoptimized
                        className="object-cover"
                        appearance="plain"
                      />
                    </div>
                    <figcaption className="mt-1 line-clamp-2 text-c12 text-t2">
                      {x.snapshot.caption}
                    </figcaption>
                  </figure>
                )}
                <div className="mt-1 -ml-2 flex items-center gap-1 text-c12">
                  <Button
                    type="button"
                    aria-pressed={x.liked}
                    aria-label={x.liked ? '좋아요 취소' : '좋아요'}
                    onClick={() => void c.like(x)}
                    className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full px-2 text-t2 tabular-nums transition-colors hover:bg-foreground/[0.08] hover:text-t1 aria-pressed:text-coral"
                    variant="bare"
                  >
                    <LuHeart
                      aria-hidden="true"
                      className={cn('size-4', x.liked && 'fill-current')}
                    />
                    {x.likes > 0 && x.likes}
                  </Button>
                  <Button
                    type="button"
                    onClick={() =>
                      setReplying({
                        rootId,
                        targetId: x.id,
                        mention: reply ? `@${x.author.name} ` : '',
                      })
                    }
                    className="h-8 cursor-pointer rounded-full px-3 font-semibold text-t2 transition-colors hover:bg-foreground/[0.08] hover:text-t1"
                    variant="bare"
                  >
                    답글
                  </Button>
                </div>
              </>
            )}
          </div>
          {editing !== x.id && (
            <CommentMenu
              items={
                mine
                  ? [
                      {
                        key: 'edit',
                        label: '수정',
                        icon: <LuPencil />,
                        onClick: () => setEditing(x.id),
                      },
                      {
                        key: 'delete',
                        label: '삭제',
                        icon: <LuTrash2 />,
                        onClick: () => void c.remove(x),
                      },
                    ]
                  : [
                      {
                        key: 'report',
                        label: '신고',
                        icon: <LuFlag />,
                        onClick: () => void c.report(x),
                      },
                    ]
              }
            />
          )}
        </div>
        {/* 답글 입력 — 누른 댓글 바로 아래(본문 줄에 맞춰 들여 씀) */}
        {replying?.targetId === x.id && (
          <CommentInput
            key={`${x.id}:${replying.mention}`}
            className={cn('mt-2', reply ? 'pl-9' : 'pl-11')}
            small
            alwaysOpen
            autoFocus
            initial={replying.mention}
            placeholder="답글 추가…"
            submitLabel="답글"
            onSubmit={async (text) => {
              const ok = await c.post(text, { parentId: rootId })
              if (ok) expand(rootId, true)
              return ok
            }}
            onCancel={() => setReplying(null)}
          />
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      {c.failed && (
        <p className="mb-4 text-c13 text-t3">
          댓글을 더 불러오지 못했어요.{' '}
          <Button
            type="button"
            onClick={c.retry}
            className="cursor-pointer font-semibold text-point underline underline-offset-4"
            variant="bare"
          >
            다시 불러오기
          </Button>
        </p>
      )}
      {roots.length === 0 && (
        <EmptyState
          compact
          title="아직 댓글이 없어요"
          description="첫 댓글을 남겨 주세요."
        />
      )}
      <ul className="grid list-none gap-5 p-0">
        {roots.map((x) => {
          const replies = repliesOf(x.id)
          const expanded = open.has(x.id)
          return (
            <li key={x.id}>
              {row(x, x.id)}
              {replies.length > 0 && (
                <div className="mt-1 pl-11">
                  <Button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => expand(x.id, !expanded)}
                    className="-ml-3 inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-full px-3 text-c13 font-semibold text-point transition-colors hover:bg-point/10"
                    variant="bare"
                  >
                    {expanded ? '답글 숨기기' : `답글 ${replies.length}개`}
                    {expanded ? (
                      <LuChevronUp aria-hidden="true" className="size-4" />
                    ) : (
                      <LuChevronDown aria-hidden="true" className="size-4" />
                    )}
                  </Button>
                  {expanded && (
                    <ul className="mt-2 grid list-none gap-4 p-0">
                      {replies.map((r) => (
                        <li key={r.id}>{row(r, x.id)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {c.hasMore && (
        <div className="mt-4 text-center">
          <Button
            type="button"
            onClick={() => void c.loadMore()}
            className="cursor-pointer text-c13 font-semibold text-t2 transition-colors hover:text-t1"
            variant="bare"
          >
            댓글 더 보기
          </Button>
        </div>
      )}
    </div>
  )
}
